/* eslint-disable @typescript-eslint/no-unused-vars */
import { useLazyQuery, useMutation } from '@apollo/client'
import { useTheme } from '@material-ui/core'
import { Form, Formik } from 'formik'
import React, { useEffect, useState } from 'react'
import { useHistory, useParams } from 'react-router-dom'
import { toast } from 'react-toastify'

import AdotelSkeleton from '../../components/AdotelSkeleton'
import {
  ACCOMMODATION_MODIFY_MUTATION,
  ACCOMMODATION_GENERATE_MUTATION,
  ACCOMMODATION_QUERY
} from '../../graphql/accommodation'
import apolloClient from '../../graphql/apolloClient'
import { countries } from '../../helpers/countriesList'
import { getRoleFromStorage } from '../../helpers/userData'
import { lang } from '../../localization'
import { ROLE } from '../../staticRoleManagement/types'
import { Accommodation } from '../../types/accommodation'
import { AccommodationAddressDetails } from './AccommodationAddressDetails'
import { AccommodationBasicDetails } from './AccommodationBasicDetails'
import { AccommodationContactDetails } from './AccommodationContactDetails'
import { AccommodationFooter } from './AccommodationFooter'
import { AccommodationInventoryAndBookingDetails } from './AccommodationInventoryAndBookingSDetails'
import { AccommodationWBSDetails } from './AccommodationWBSDetails'
import useStyles from './styles/AccommodationMainForm.styles'
import { accommodationFormValidationSchema } from './validations/accommodationFormValidation'

const ACCOMMODATION_FORM_INIT_VALUES: Accommodation = {
  basicDetails: {
    name: '',
    nameTranslatedList: [
      { label: 'Iran, Islamic Republic of', value: '', nativeLabel: 'فارسی' }
    ],
    propertyCode: '',
    displayPropertyCode: '',
    resourceType: 'Hotel',
    website: '',
    description: '',
    descriptionTranslatedList: [
      { label: 'Iran, Islamic Republic of', value: '', nativeLabel: 'فارسی' }
    ],
    starClass: 0,
    timeZone: '',
    tenantCode: '',
    tenantName: '',
    totaRoomNumber: 0,
    accommodationID: '',
    tenantId: '',
    defaultProviderCode: '',
    checkinFrom: '00:00',
    checkinTo: '00:00',
    checkoutFrom: '00:00',
    checkoutTo: '00:00'
  },
  addressDetails: {
    country: {
      label: '',
      value: '',
      id: '',
      code: ''
    },
    city: '',
    postalCode: '',
    address: '',
    addressTranslatedList: [
      { label: 'Iran, Islamic Republic of', value: '', nativeLabel: 'فارسی' }
    ],
    latitude: 0.0,
    longitude: 0.0
  },
  contactDetails: {
    email: '',
    bookingConfirmationEmail: '',
    bookingFailReportEmail: '',
    bookingConfirmationPhoneNumber: '',
    bookingFailReportPhoneNumber: '',
    fax: '',
    bookingConfirmationFax: ''
  },
  wbsDetails: {
    overrideRedirect: false,
    bookingRedirect: ''
  },
  inventoryAndBooking: {
    currencies: [],
    canReserveCombination: true
  }
}

const AccommodationMainForm = () => {
  const classes = useStyles()
  const history = useHistory()
  const params = useParams<{ propertyCode: string }>()
  const theme = useTheme()

  const [accommodationOnSubmitFunction, { loading: loadingForAccommodation }] =
    useMutation(
      params.propertyCode
        ? ACCOMMODATION_MODIFY_MUTATION
        : ACCOMMODATION_GENERATE_MUTATION
    )

  const [initialValues, setinitialValues] = useState<Accommodation>(
    ACCOMMODATION_FORM_INIT_VALUES
  )

  const [getInitialValues, { loading }] = useLazyQuery<{
    accommodation: Accommodation
  }>(ACCOMMODATION_QUERY, {
    notifyOnNetworkStatusChange: true,
    fetchPolicy: 'network-only',
    onCompleted: (data) => {
      const selectedCountry = countries.find((country) =>
        country.label
          .toLocaleLowerCase()
          .includes(
            data.accommodation.addressDetails.country.label.toLocaleLowerCase()
          )
      )

      setinitialValues({
        ...data.accommodation,
        addressDetails: {
          ...data.accommodation.addressDetails,
          country: {
            value: selectedCountry?.code || '',
            label: selectedCountry?.label || '',
            id: selectedCountry?.code || '',
            code: selectedCountry?.phone || ''
          }
        }
      })
    },
    onError: (error) => {
      toast.error(error.message)
    }
  })

  const handleSubmit = async (values: Accommodation) => {
    const {
      accommodationID,
      defaultProviderCode,
      displayPropertyCode,
      tenantName,
      tenantCode,
      displayTenantCode,
      ...newBasicDetails
    } = values.basicDetails
    const newValues = { ...values, basicDetails: newBasicDetails }

    try {
      const { data: response } = await accommodationOnSubmitFunction(
        params.propertyCode
          ? {
              variables: {
                input: newValues,
                propertyCode: params.propertyCode
              }
            }
          : {
              variables: {
                input: newValues
              }
            }
      )
      if (params.propertyCode) {
        toast.success(response.modifyAccommodation.message)
        history.push(`/${lang}/accommodations`)
      } else {
        toast.success(response.generateAccommodation.message)
        history.push(`/${lang}/accommodations`)
      }
      apolloClient.cache.evict({
        fieldName: 'accommodations'
      })
    } catch (error) {
      if (error instanceof Error) {
        toast.error(error.message)
      } else {
        toast.error('ERROR!')
      }
    }
  }

  useEffect(() => {
    if (params.propertyCode) {
      getInitialValues({
        variables: {
          propertyCode: params.propertyCode
        }
      })
    }
  }, [])

  const validAccommodationsRoles = [
    ROLE.ACCOMMODATION,
    ROLE.ACCOMMODATION_OPERATOR
  ]

  const isRoleAccommodation = validAccommodationsRoles.some(
    (role) => role === getRoleFromStorage()
  )

  return (
    <Formik
      enableReinitialize
      initialValues={initialValues}
      onSubmit={handleSubmit}
      validationSchema={accommodationFormValidationSchema}>
      {({
        values,
        setFieldValue,
        errors,
        touched,
        handleBlur,
        isSubmitting
      }) => {
        return (
          <>
            <Form>
              <AccommodationBasicDetails
                loading={loading}
                isForModify={!!params.propertyCode}
                {...{
                  values,
                  setFieldValue,
                  errors,
                  touched,
                  handleBlur,
                  isSubmitting,
                  classes
                }}
              />
              <AccommodationAddressDetails
                loading={loading}
                {...{
                  values,
                  setFieldValue,
                  errors,
                  touched,
                  handleBlur,
                  isSubmitting,
                  classes
                }}
              />
              <AccommodationContactDetails
                {...{
                  values,
                  setFieldValue,
                  errors,
                  touched,
                  handleBlur,
                  isSubmitting,
                  classes
                }}
              />

              {/* <AccommodationChildrenDetails
              {...{
                values,
                setFieldValue,
                errors,
                touched,
                handleBlur,
                isSubmitting,
                classes,
                setCheckChildrenRange
              }}
            /> */}
              {!isRoleAccommodation && (
                <>
                  {/* <AccommodationWBSDetails
                    {...{
                      values,
                      setFieldValue,
                      errors,
                      touched,
                      handleBlur,
                      isSubmitting,
                      classes
                    }}
                  /> */}
                  <AccommodationInventoryAndBookingDetails
                    loading={loading}
                    {...{
                      values,
                      setFieldValue,
                      errors,
                      touched,
                      handleBlur,
                      isSubmitting,
                      classes
                    }}
                  />
                </>
              )}
              <AccommodationFooter
                isSubmitting={isSubmitting}
                loadingForAccommodation={loadingForAccommodation}
              />
            </Form>
            <AdotelSkeleton
              open={loading}
              circlesColor={theme.palette.primary.main}
              numberOfCircles={4}
            />
          </>
        )
      }}
    </Formik>
  )
}

export default AccommodationMainForm
